import { createSlice } from "@reduxjs/toolkit";

import {
  fetchCandidateDetails,
  updateHRNotes,
  scheduleInterview,
} from "./candidateDetails.thunk";

import { updateCandidateStatus } from "./candidates.thunk";

import { CandidateDetailsResponse } from "../types";
import type { StatusHistoryItem } from "../types/common.types";

interface CandidateDetailsState {
  data: CandidateDetailsResponse | null;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
}

const initialState: CandidateDetailsState = {
  data: null,
  loading: false,
  refreshing: false,
  error: null,
};
const candidateDetailsSlice = createSlice({
  name: "candidateDetails",

  initialState,

  reducers: {
    clearCandidateDetails: (state) => {
      state.data = null;
      state.loading = false;
      state.refreshing = false;
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder

      // --------------------------------------------------
      // Fetch Candidate Details
      // --------------------------------------------------

      .addCase(
        fetchCandidateDetails.pending,
        (state) => {
          if (state.data) {
            state.refreshing = true;
          } else {
            state.loading = true;
          }

          state.error = null;
        }
      )

      .addCase(
        fetchCandidateDetails.fulfilled,
        (state, action) => {
          state.loading = false;
          state.refreshing = false;
          state.data = action.payload;
          state.error = null;
        }
      )

      .addCase(
        fetchCandidateDetails.rejected,
        (state, action) => {
          state.loading = false;
          state.refreshing = false;

          state.error =
            (action.payload as string) ||
            "Unable to fetch candidate details";
        }
      )

      // --------------------------------------------------
      // Update HR Notes
      // --------------------------------------------------

      .addCase(
        updateHRNotes.fulfilled,
        (state, action) => {
          if (
            state.data?.candidateData?.application
          ) {
            state.data.candidateData.application.hrNotes =
              action.payload.hrNotes;
          }
        }
      )

      // --------------------------------------------------
      // Schedule Interview
      // --------------------------------------------------

      .addCase(
        scheduleInterview.fulfilled,
        (state, action) => {
          if (
            state.data?.candidateData?.application
          ) {
            state.data.candidateData.application.interviewDetails = {
              ...state.data.candidateData.application
                .interviewDetails,

              ...action.payload.interviewDetails,

              scheduled: true,
            };
          }
        }
      )

      // --------------------------------------------------
      // Update Candidate Status
      // --------------------------------------------------

      .addCase(
        updateCandidateStatus.fulfilled,
        (state, action) => {
          const updatedApplicationId =
            action.payload?.data?.applicationId;

          const updatedHistory =
            action.payload?.data?.statusHistory;

          const currentApplication =
            state.data?.candidateData?.application;

          const currentApplicationId =
            currentApplication?.id;

          if (
            !updatedApplicationId ||
            !updatedHistory ||
            !currentApplication ||
            currentApplicationId !==
            updatedApplicationId
          ) {
            return;
          }

          const statusHistory =
            updatedHistory as StatusHistoryItem[];

          currentApplication.statusHistory =
            statusHistory;

          currentApplication.status =
            statusHistory as any;

          if (state.data?.candidateData) {
            state.data.candidateData.statusHistory =
              statusHistory;
          }
        }
      );
  },
});

export const {
  clearCandidateDetails,
} = candidateDetailsSlice.actions;

export default candidateDetailsSlice.reducer;